import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";

@Component({
  selector: "up-select-list",
  templateUrl: "./select-list.component.html",
  styleUrls: ["./select-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectListComponent implements OnChanges {
  invertSelectedChecked: boolean = false;
  invertNotSelectedChecked: boolean = false;

  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() itemsList: any[] = [];
  @Input() selectedsIds: any[] = [];
  @Input() itemFilterFields: string[] = [];
  @Input() itemTemplate!: TemplateRef<any>;

  @Output() selectionChanged: EventEmitter<any[]> = new EventEmitter<any[]>();

  @ViewChild("notSelectedSearch", { static: false })
  notSelectedSearch!: ElementRef;
  @ViewChild("selectedSearch", { static: false }) selectedSearch!: ElementRef;

  constructor() {}

  ngOnInit() {
    this.initializeItemsList();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["itemsList"] &&
      !this.arraysEqual(
        changes["itemsList"].currentValue,
        changes["itemsList"].previousValue,
      )
    ) {
      this.updateItemsList();
    }

    if (
      changes["selectedsIds"] &&
      !this.arraysEqual(
        changes["selectedsIds"].currentValue,
        changes["selectedsIds"].previousValue,
      )
    ) {
      this.updateSelectedItems();
    }
  }

  private initializeItemsList() {
    this.itemsList.forEach((item) => {
      item.checked = false;
      item.selected = this.selectedsIds.includes(item.id) ?? false;
      item.filtered = true;
    });
  }

  private updateItemsList() {
    this.itemsList.forEach((item) => {
      item.checked = false;
      item.selected = this.selectedsIds.includes(item.id) ?? false;
      item.filtered = true;
    });

    if (this.selectedsIds.length) {
      this.selectChange();
    }
  }

  private updateSelectedItems() {
    this.itemsList.forEach((item) => {
      item.checked = false;
      item.selected = this.selectedsIds.includes(item.id) ?? false;
      item.filtered = true;
    });
  }

  filter(event: any, selected: boolean) {
    const value = event.target.value.toLowerCase();

    let items = this.itemsList.filter((x) => x.selected === selected);

    if (value) {
      let idsOk: number[] = [];

      for (const itemFilterField of this.itemFilterFields) {
        let ids: number[] = [];

        if (itemFilterField.includes(".")) {
          let parts = itemFilterField.split(".");

          switch (parts.length) {
            case 2:
              ids = items
                .filter((x) => {
                  if (x[parts[0]] && typeof x[parts[0]] === "object") {
                    if (
                      x[parts[0]][parts[1]] !== undefined &&
                      x[parts[0]][parts[1]] !== null
                    ) {
                      return x[parts[0]][parts[1]]
                        .toString()
                        .toLowerCase()
                        .includes(value);
                    }
                  }
                })
                .map((x) => x.id);
              break;
            case 3:
              ids = items
                .filter((x) => {
                  return x[parts[0]][parts[1]][parts[2]]
                    .toString()
                    .toLowerCase()
                    .includes(value);
                })
                .map((x) => x.id);
              break;
            case 4:
              ids = items
                .filter((x) => {
                  return x[parts[0]][parts[1]][parts[2]][parts[3]]
                    .toString()
                    .toLowerCase()
                    .includes(value);
                })
                .map((x) => x.id);
              break;
            case 5:
              ids = items
                .filter((x) => {
                  return x[parts[0]][parts[1]][parts[2]][parts[3]][parts[4]]
                    .toString()
                    .toLowerCase()
                    .includes(value);
                })
                .map((x) => x.id);
              break;
            default:
              break;
          }
        } else {
          ids = items
            .filter((x) =>
              x[itemFilterField].toString().toLowerCase().includes(value),
            )
            .map((x) => x.id);
        }

        ids.forEach((id) => {
          if (!idsOk.includes(id)) {
            idsOk.push(id);
          }
        });
      }

      items.forEach((item) => {
        item.filtered = idsOk.includes(item.id);
      });
    } else {
      items.forEach((item) => {
        item.filtered = true;
      });
    }
  }

  check(item: any) {
    item.checked = !item.checked;
  }

  invertCheck(selected: boolean) {
    const items = this.itemsList.filter((x) => x.selected === selected);

    if (selected) {
      this.invertSelectedChecked = !this.invertSelectedChecked;
    } else {
      this.invertNotSelectedChecked = !this.invertNotSelectedChecked;
    }

    items.forEach((item) => {
      item.checked = !item.checked;
    });
  }

  addChecked() {
    const items = this.itemsList.filter((x) => !x.selected);

    if (items.length) {
      items.forEach((item) => {
        item.filtered = true;
      });

      this.notSelectedSearch.nativeElement.value = "";
      this.notSelectedSearch.nativeElement.focus();
    }

    const itemsChecked = items.filter((x) => x.checked);

    itemsChecked.forEach((item) => {
      item.checked = false;
      item.selected = true;
    });

    this.invertNotSelectedChecked = false;
    this.selectChange();
  }

  removeChecked() {
    const items = this.itemsList.filter((x) => x.selected);

    if (items.length) {
      items.forEach((item) => {
        item.filtered = true;
      });

      this.selectedSearch.nativeElement.value = "";
      this.selectedSearch.nativeElement.focus();
    }

    const itemsChecked = items.filter((x) => x.checked);

    itemsChecked.forEach((item) => {
      item.checked = false;
      item.selected = false;
    });

    this.invertSelectedChecked = false;
    this.selectChange();
  }

  addAll() {
    this.notSelectedSearch.nativeElement.value = "";

    const items = this.itemsList.filter((x) => !x.selected);

    items.forEach((item) => {
      item.checked = false;
      item.selected = true;
    });

    this.invertNotSelectedChecked = false;
    this.selectChange();
  }

  removeAll() {
    this.selectedSearch.nativeElement.value = "";

    const items = this.itemsList.filter((x) => x.selected);

    items.forEach((item) => {
      item.checked = false;
      item.selected = false;
    });

    this.invertSelectedChecked = false;
    this.selectChange();
  }

  getFilteredItems(selected: boolean) {
    return this.itemsList.filter((x) => x.selected === selected && x.filtered);
  }

  getAllItems(selected: boolean) {
    return this.itemsList.filter((x) => x.selected === selected);
  }

  selectChange() {
    this.selectionChanged.emit(this.getAllItems(true));
  }

  trackByFn(index: number, item: any) {
    return item.id; // or any unique identifier
  }

  private arraysEqual(a: any[], b: any[]): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }
}
